/**
 * Search Presentation Images Tool
 *
 * Semantic search within presentation deck for images.
 * Uses vector embeddings to find visually/semantically similar images.
 *
 * NOTE: This tool only returns metadata (slideId, shapeId, description).
 * The actual image extraction happens in retrievePresentationImage via COM,
 * which gets the LIVE image from PowerPoint (not stale indexed data).
 */
import { ToolDescription, safeParseToolParams } from "../../../../base/toolDescription";
import { ToolBase } from "../../../../base/toolBase";
import { backendClient } from "../../../../../../services/communication/backendClient";
import { getOrCreatedocumentId } from "../../../../../../services/documentIdManager";
import { SearchPresentationImagesParamsSchema, } from "./searchPresentationImages.schema";
// ═══════════════════════════════════════════════════════════════════════════
// DECK ID RESOLUTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Resolve deck ID from various input formats.
 *
 * @param documentId - Numeric index, "CURRENT", or explicit deck ID
 * @returns Resolved deck ID string
 */
function unwrapQuotedSelector(value) {
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
        const quoteWrapped = (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
            (trimmed.startsWith("'") && trimmed.endsWith("'"));
        if (quoteWrapped) {
            return trimmed.slice(1, -1).trim();
        }
    }
    if (trimmed.length >= 4) {
        const escapedQuoteWrapped = (trimmed.startsWith('\\"') && trimmed.endsWith('\\"')) ||
            (trimmed.startsWith("\\'") && trimmed.endsWith("\\'"));
        if (escapedQuoteWrapped) {
            return trimmed.slice(2, -2).trim();
        }
    }
    return trimmed;
}
function normalizeDocumentSelector(value) {
    if (typeof value === "number") {
        return value;
    }
    let normalized = value.trim();
    for (let i = 0; i < 3; i++) {
        const unwrapped = unwrapQuotedSelector(normalized);
        if (unwrapped === normalized)
            break;
        normalized = unwrapped;
    }
    if (/^\d+$/.test(normalized)) {
        return Number.parseInt(normalized, 10);
    }
    if (normalized.toUpperCase() === "CURRENT")
        return "CURRENT";
    if (normalized.toUpperCase() === "ALL")
        return "ALL";
    return normalized;
}
async function resolvedocumentId(documentId) {
    const normalizedSelector = normalizeDocumentSelector(documentId);
    if (normalizedSelector !== documentId) {
        console.warn(`[SearchPresentationImages] Normalized documentId from "${String(documentId)}" to "${String(normalizedSelector)}"`);
    }
    // Handle "CURRENT" - get the current presentation's deck ID
    if (normalizedSelector === "CURRENT") {
        try {
            const currentdocumentId = await getOrCreatedocumentId();
            console.log(`[SearchPresentationImages] Resolved CURRENT to deck: ${currentdocumentId}`);
            return currentdocumentId;
        }
        catch (error) {
            console.error(`[SearchPresentationImages] Failed to get current deck ID:`, error);
            throw new Error('Unable to determine current presentation deck ID');
        }
    }
    // Handle numeric index - lookup from PowerPoint File Index Registry
    if (typeof normalizedSelector === 'number') {
        // TODO: Implement index lookup from registry
        // For now, return a placeholder format
        console.log(`[SearchPresentationImages] Using deck index: ${normalizedSelector}`);
        return `deck_index_${normalizedSelector}`;
    }
    // String deck ID - use as-is
    return normalizedSelector;
}
// ═══════════════════════════════════════════════════════════════════════════
// SEARCH PRESENTATION IMAGES TOOL
// ═══════════════════════════════════════════════════════════════════════════
// System prompt fallback for Keywords AI
const SEARCH_PRESENTATION_IMAGES_SYSTEM_PROMPT = `### searchPresentationImages
Semantic search for images within PowerPoint presentations.

**When to use:**
- Finding images in the current or other indexed presentations
- Looking for specific visuals (charts, photos, diagrams)
- Finding images by description/content

**NOTE:** Returns metadata only (slideId, shapeId, description).
Call retrievePresentationImage to actually download the image.

\`\`\`json
{
  "documentId": "CURRENT",
  "query": "revenue chart",
  "limit": 5
}
\`\`\`

Returns: matching images with slide/shape IDs and descriptions.`;
/**
 * Tool for semantic search of images within presentations.
 *
 * Uses the Python backend's vector search to find images
 * that match the query semantically.
 */
export class SearchPresentationImagesTool extends ToolBase {
    constructor() {
        super();
        this.name = "searchPresentationImages";
        // System prompt description
        this.systemPromptDescription = SEARCH_PRESENTATION_IMAGES_SYSTEM_PROMPT;
        // Streaming operation fields
        this.fieldsToCheck = [
            { name: 'uiDescription', type: 'string', list_status: false, order: 0 },
            { name: 'documentId', type: 'string', list_status: false, order: 1 },
            { name: 'query', type: 'string', list_status: false, order: 2 },
            { name: 'limit', type: 'number', list_status: false, order: 3 },
        ];
        this.listsToCheck = [];
        this.description = new ToolDescription("searchPresentationImages", `Search within presentation deck for images using semantic search.

**Purpose:** Find images in existing presentations that match your needs.

**Parameters:**
- documentId: Which deck to search:
  - Numeric index (0, 1, 2...) from PowerPoint File Index Registry
  - "CURRENT" for the active presentation
  - Explicit deck ID string
- query: Semantic search query
- limit: Maximum results (default: 10)

**Example:**
\`\`\`json
{
  "documentId": "CURRENT",
  "query": "Deutsche Bank logo",
  "limit": 10
}
\`\`\`

**Returns:**
- results: Array of matching images with:
  - slideId: Slide containing the image
  - shapeId: Shape ID within the slide
  - description: Existing LLM description
  - similarity: 0-1 semantic similarity score

**Note:** Use retrievePresentationImage to extract the actual image.`, SearchPresentationImagesParamsSchema);
    }
    async execute(parameters) {
        const parseResult = safeParseToolParams(SearchPresentationImagesParamsSchema, parameters);
        if (!parseResult.success) {
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }
        const params = parseResult.data;
        const documentIdStr = typeof params.documentId === 'number'
            ? `index ${params.documentId}`
            : params.documentId;
        console.log(`[SearchPresentationImagesTool] Searching deck ${documentIdStr} for "${params.query}" (limit: ${params.limit})`);
        try {
            // Resolve the deck ID
            const resolveddocumentId = await resolvedocumentId(params.documentId);
            console.log(`[SearchPresentationImagesTool] Resolved deck ID: ${resolveddocumentId}`);
            // Call the backend search
            const searchResult = await backendClient.searchImages(resolveddocumentId, params.query, params.limit ?? 10);
            if (!searchResult.success) {
                console.error(`[SearchPresentationImagesTool] Backend search failed:`, searchResult.error);
                return {
                    success: false,
                    query: params.query,
                    results: [],
                    totalFound: 0,
                    error: searchResult.error || 'Search failed',
                };
            }
            // Transform backend results to tool response format
            const results = searchResult.results.map(r => ({
                slideId: r.slide_id,
                shapeId: r.shape_id,
                description: r.description,
                similarity: r.similarity,
                documentId: resolveddocumentId,
            }));
            console.log(`[SearchPresentationImagesTool] ✅ Found ${results.length} image results`);
            return {
                success: true,
                query: params.query,
                results,
                totalFound: results.length,
            };
        }
        catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            console.error("[SearchPresentationImagesTool] ❌ Error:", error);
            return {
                success: false,
                query: params.query,
                results: [],
                totalFound: 0,
                error: errorMsg,
            };
        }
    }
}
